<template>
  <div v-if="isTeaserVisible">
    <figure
      :class="[
        $style.container,
        { ResultTeaserArticle__shopContainer: isShop }
      ]"
      :style="{backgroundImage: `url(${bgImgUrl})` }"
    >
      <!-- IMAGE -->
      <div :class="$style.imageLink">
        <a :href="`${article.shopUrl}&itemId=${articleNumber}`">
          <div
            :class="$style.bgImage"
            :style="{ backgroundImage: `url(${article.image})` }"
          >
            <img :src="article.image" :alt="article.title" />
            <i
             v-if="article.archived"
              :class="['fa fa-archive', $style.archived]"
            />
          </div>
        </a>
      </div>

      <!-- DETAILS -->
      <figcaption>
        <a :href="`${article.shopUrl}&itemId=${articleNumber}`">
          <div :class="$style.supline">
            {{ dictionary.articleNumber }} {{ shopArticle.ItemId }} -
            {{ renderTypesLabel() }}
            {{ renderTypes() }}
          </div>
        </a>
        <a :href="`${article.shopUrl}&itemId=${articleNumber}`">
          <h3 :class="$style.textLink" v-html="article.title" />
        </a>
        <a :href="`${article.shopUrl}&itemId=${articleNumber}`">
          <div :class="$style.textLink" v-html="article.description" />
        </a>
        <div :class="$style.linkDetail" @click="openModal">
          {{ dictionary.details }}
        </div>

        <!-- SHOP -->
        <div :class="$style.shop">
          <!-- PRICE -->
          <div :class="$style.shopPrice">
            <strong>{{ priceSum() }}</strong>
            <span
              >{{ dictionary.plusVat }} &amp;
              <a target="_blank" :href="shopLinkURL + '/' + shippingURL">{{ dictionary.shipping }}</a></span
            >
          </div>

          <!-- SHOPCARTLINK -->
          <ShopCartLink
            v-if="isShop"
            :key="`cartLink-${shopArticle.ItemId}`"
            :item="{
              text: dictionary.addToCart,
              quantity: '1',
              id: shopArticle.ItemId,
              headline: article.title,
              subheadline: article.subHeadline,
              pictureurl: article.image,
              thumbnailurl: article.image,
              producturl: article.shopUrl
            }"
            :addItemFailedMsg="dictionary.addItemFailed"
            :addButtonLoadingText="dictionary.addToCart"
          />
          <Button
            v-else
            colour="orange"
            :href="`${article.shopUrl}&itemId=${articleNumber}`"
            :text="dictionary.goToOnlineShop"
            isLink
          />
        </div>
      </figcaption>
    </figure>

    <!-- MODAL -->
    <Modal :isModalOpen="isModalOpen" @closeModal="closeModal">
      <div :class="$style.modal">
        <p :class="$style.heading">
          {{ dictionary.articleNumber }} {{ articleNumber }}
        </p>
        <div class="columns is-gapless">
          <div class="column is-two-thirds-tablet">
            <p :class="$style.subHeading">{{ dictionary.details }}</p>
            <p
              v-html="
                replaceNewLine(shopArticle.Longtext || shopArticle.Shorttext)
              "
            ></p>
          </div>
        </div>
      </div>
    </Modal>
  </div>
  <div v-else class="preloadFilter">
    <Loading />
  </div>
</template>

<script>
import axios from "axios";
import Button from "../Button/Button.vue";
import Loading from "../Loading/Loading";
import ShopCartLink from "../ShopCartLink/ShopCartLink";
import Modal from "../Modal/Modal";
import { Category } from "../../globals/constants/search";
import { replaceNewLine } from "../../utils/helper";

export default {
  name: "ResultTeaserArticle",
  components: {
    Button,
    Loading,
    Modal,
    ShopCartLink
  },
  props: {
    articleNumber: {
      type: String
    },
    bgImgUrl: {
      type: String
    },
    shopBaseUrl: {
      type: String
    },
    result: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dictionary: window.dictionary,
      isModalOpen: false,
      isShop: window.instance === "shop",
      lastShopArticle: undefined,
      shippingURL: window.shippingURL,
      shopLinkURL: window.shopStartpageUrl,
      shopArticle: undefined,
      replaceNewLine
    };
  },
  computed: {
    isTeaserVisible() {
      return (
        this.article &&
        this.shopArticle &&
        this.articleNumber === this.shopArticle.ItemId
      );
    },
    article() {
      const articles = this.result.filter(
        item =>
          item._source.categoryuid === Category.PRODUCTS &&
          item._source.articles &&
          item._source.articles.indexOf(this.articleNumber) !== -1
      );

      if (articles.length > 0) {
        const { _id: id, _source } = articles[0];

        return {
          id,
          ..._source
        };
      }

      return undefined;
    }
  },
  watch: {
    article: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!newValue) {
          return;
        }

        if (!oldValue || newValue.id !== oldValue.id) {
          this.getShopArticle();
        }
      }
    }
  },
  methods: {
    openModal() {
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    priceSum: function() {
      const { PositionPrice, Quantity } = this.shopArticle;
      const totalPrice = PositionPrice * (Quantity || 1);

      return accounting.formatMoney(totalPrice, {
        symbol: window.currency,
        format: window.currencyPosition,
        decimal: window.decimalSep,
        thousand: window.thousandSep,
        precision: window.precisionValue
      });
    },
    renderTypesLabel() {
      const { MainType: type } = this.shopArticle;
      const { productType, productTypes } = dictionary;

      return type.trim().split(" ").length > 1 ? productTypes : productType;
    },
    renderTypes() {
      const { MainType: type } = this.shopArticle;

      return type
        .split(" ")
        .filter((value, index, self) => self.indexOf(value) === index)
        .join(", ");
    },
    getShopArticle() {
      if (
        !this.article ||
        !this.articleNumber ||
        this.articleNumber === this.lastShopArticle
      ) {
        return;
      }

      this.lastShopArticle = this.articleNumber;

      axios
        .post(window.getItemInfoUrl, {
          // ? https://shop.wika.com/rest-services/shop/getiteminfos
          country: window.currentCountry,
          itemid: this.articleNumber,
          language: window.currentLanguage
        })
        .then(response => {
          if (response.data.Success) {
            this.shopArticle = response.data;
          }
        })
        .catch(() => {
          this.shopArticle = undefined;
        });
    }
  }
};
</script>

<style module lang="scss">
@import "./ResultTeaserArticle.scss";
</style>
